import { CalendarOutlined, FileTextOutlined } from "@ant-design/icons";
import {
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Typography,
  message,
} from "antd";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import React, { useEffect } from "react";

import { IStationeryPeriod } from "../../../models/Hrm";
import {
  useCreateStationeryPeriod,
  useUpdateStationeryPeriod,
} from "../../../query/HrmQuery";
import { notify } from "../../../utils/toast";

const { Text } = Typography;

interface Props {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  initialData?: IStationeryPeriod | null;
}

export const ModalAddStationery: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  initialData,
}) => {
  const createMutation = useCreateStationeryPeriod();
  const updateMutation = useUpdateStationeryPeriod();
  const [form] = Form.useForm();

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        form.setFieldsValue({
          name: initialData.name,
          startDate: dayjs(initialData.startDate),
          endDate: dayjs(initialData.endDate),
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          startDate: dayjs(),
          endDate: dayjs().add(1, "day"),
        });
      }
    }
  }, [isOpen, initialData, form]);

  const handleSubmit = async (values: any) => {
    try {
      const payload = {
        ...values,
        startDate: values.startDate.toISOString(),
        endDate: values.endDate.toISOString(),
      };

      if (initialData) {
        await updateMutation.mutateAsync({ id: initialData.id, data: payload });
        message.success("Cập nhật kỳ văn phòng phẩm thành công");
      } else {
        await createMutation.mutateAsync(payload);
        message.success("Tạo kỳ văn phòng phẩm thành công");
      }

      setIsOpen(false);
      form.resetFields();
    } catch (error: unknown) {
      const err = error as AxiosError<{ message: string }>;
      const msg =
        err.response?.data?.message ||
        (initialData ? "Cập nhật thất bại" : "Tạo kỳ văn phòng phẩm thất bại");
      notify.error(msg);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      title={
        <Typography.Title level={4}>
          {initialData ? "Cập nhật kỳ văn phòng phẩm" : "Tạo kỳ văn phòng phẩm"}
        </Typography.Title>
      }
      open={isOpen}
      onCancel={() => setIsOpen(false)}
      onOk={() => {
        if (isPending) return;
        form.submit();
      }}
      okText={initialData ? "Cập nhật" : "Tạo mới"}
      cancelText="Hủy"
      confirmLoading={isPending}
      width={600}
      centered
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 24 }}>
        Nhập thông tin kỳ mua văn phòng phẩm
      </Typography.Paragraph>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Row gutter={16}>
          {/* Tên */}
          <Col span={24}>
            <Form.Item
              name="name"
              label={
                <Space>
                  <FileTextOutlined className="text-blue-500" />
                  <Text strong>Tên kỳ</Text>
                </Space>
              }
              rules={[{ required: true, message: "Vui lòng nhập tên" }]}
            >
              <Input placeholder="VD: Văn phòng phẩm tháng 5" />
            </Form.Item>
          </Col>

          {/* Start */}
          <Col span={12}>
            <Form.Item
              name="startDate"
              label={
                <Space>
                  <CalendarOutlined className="text-blue-500" />
                  <Text strong>Ngày bắt đầu</Text>
                </Space>
              }
              dependencies={["endDate"]}
              rules={[
                { required: true, message: "Chọn ngày bắt đầu" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const endDate = getFieldValue("endDate");
                    if (
                      !value ||
                      !endDate ||
                      dayjs(value).isBefore(dayjs(endDate))
                    ) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("Ngày bắt đầu phải nhỏ hơn ngày kết thúc"),
                    );
                  },
                }),
              ]}
            >
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>

          {/* End */}
          <Col span={12}>
            <Form.Item
              name="endDate"
              label={
                <Space>
                  <CalendarOutlined className="text-blue-500" />
                  <Text strong>Ngày kết thúc</Text>
                </Space>
              }
              dependencies={["startDate"]}
              rules={[
                { required: true, message: "Chọn ngày kết thúc" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const startDate = getFieldValue("startDate");
                    if (
                      !value ||
                      !startDate ||
                      dayjs(value).isAfter(dayjs(startDate))
                    ) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("Ngày kết thúc phải lớn hơn ngày bắt đầu"),
                    );
                  },
                }),
              ]}
            >
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
